/**
 * ResumePage Component
 * Candidate Registration Form with all required fields
 */

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PopupMessage from "./PopupMessage";
import { useAuth } from "../AuthContext";
import { registerCandidate, updateCandidate } from "../api";
import {
  currentLocations,
  primarySkillOptions,
  secondarySkillOptions,
  workTypeOptions,
  roleOptions,
} from "../services/dropdownOptions";

const ResumePage = () => {
  // ============================================================
  // STATE MANAGEMENT
  // ============================================================

  const location = useLocation();
  const navigate = useNavigate();
  const candidateData = location.state?.candidateData;
  const isEditMode = location.state?.isEditMode || false;

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    currentLocation: "",
    readyToTravel: "No",
    yearsOfExperience: "",
    primarySkills: "",
    secondarySkills: [],
    techStack: "",
    workType: [],
    noticePeriod: "",
    c2c: "",
    expectedC2CRate: "",
    freelanceRate: "",
    referrerCode: "",
    source: "",
    status: "Active",
    role: "",
    aadharNo: "",
    pancardNo: "",
    remoteAvailable: "No",
    availableFrom: "",
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [secondarySearch, setSecondarySearch] = useState("");
  const [workTypeSearch, setWorkTypeSearch] = useState("");
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [workTypeOpen, setWorkTypeOpen] = useState(false);
  const secondaryDropdownRef = useRef(null);
  const workTypeDropdownRef = useRef(null);
  const [popupData, setPopupData] = useState({ visible: false, message: "", targetId: "" });
  const [loadingPopup, setLoadingPopup] = useState({ visible: false, message: "Processing registration..." });
  const [successPopup, setSuccessPopup] = useState({ visible: false, message: "", candidateId: "" });
  const { user } = useAuth();
  const authReferrerCode = user?.referrerCode || user?.referral_code || user?.referrer_code || "";

  // Pre-fill form with candidate data if in edit mode
  useEffect(() => {
    if (isEditMode && candidateData) {
      // Helper function to sanitize numeric fields
      const sanitizeNumeric = (value) => {
        if (!value) return "";
        const num = value.toString().replace(/\D/g, '');
        return num;
      };

      setFormData({
        fullName: candidateData.name || "",
        email: candidateData.email || "",
        mobile: candidateData.phone || "",
        currentLocation: candidateData.location || "",
        readyToTravel: candidateData.readyToTravel || "No",
        yearsOfExperience: candidateData.experience || "",
        primarySkills: candidateData.primarySkills || "",
        secondarySkills: Array.isArray(candidateData.secondarySkills)
          ? candidateData.secondarySkills
          : (typeof candidateData.secondarySkills === 'string' 
              ? candidateData.secondarySkills.split(',').map(s => s.trim())
              : []),
        techStack: candidateData.techStack || "",
        workType: Array.isArray(candidateData.workType)
          ? candidateData.workType
          : (typeof candidateData.workType === 'string'
              ? candidateData.workType.split(',').map(s => s.trim())
              : []),
        noticePeriod: sanitizeNumeric(candidateData.noticePeriod),
        c2c: sanitizeNumeric(candidateData.c2c),
        expectedC2CRate: sanitizeNumeric(candidateData.expectedCTC),
        freelanceRate: sanitizeNumeric(candidateData.freelanceRate),
        referrerCode: candidateData.referrerCode || "",
        source: candidateData.source || "",
        status: candidateData.status || "Active",
        role: candidateData.role || "",
        aadharNo: candidateData.aadharNo && candidateData.aadharNo !== 'Not specified' ? candidateData.aadharNo : "",
        pancardNo: candidateData.pancardNo && candidateData.pancardNo !== 'Not specified' ? candidateData.pancardNo : "",
        remoteAvailable: candidateData.remoteAvailable || "No",
        availableFrom: candidateData.availableFrom || "",
      });
    } else if (authReferrerCode && !formData.referrerCode) {
      setFormData((prev) => ({
        ...prev,
        referrerCode: authReferrerCode,
      }));
    }
  }, [isEditMode, candidateData, authReferrerCode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (secondaryOpen && secondaryDropdownRef.current && !secondaryDropdownRef.current.contains(event.target)) {
        setSecondaryOpen(false);
      }
      if (workTypeOpen && workTypeDropdownRef.current && !workTypeDropdownRef.current.contains(event.target)) {
        setWorkTypeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [secondaryOpen, workTypeOpen]);

  // ============================================================
  // VALIDATION FUNCTIONS
  // ============================================================

  /**
   * Validate all form fields
   */
  const validateForm = (data = formData) => {
    const newErrors = {};

    // Required fields validation
    if (!data.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
    }

    if (!data.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!data.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(data.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
    }

    if (!data.currentLocation) {
      newErrors.currentLocation = "Current Location is required";
    }

    if (!data.yearsOfExperience) {
      newErrors.yearsOfExperience = "Years of Experience is required";
    } else if (isNaN(parseFloat(data.yearsOfExperience))) {
      newErrors.yearsOfExperience = "Please enter a valid numeric value for years of experience";
    } else if (parseFloat(data.yearsOfExperience) > 30) {
      newErrors.yearsOfExperience = "Years of Experience cannot exceed 30";
    }

    if (!data.primarySkills) {
      newErrors.primarySkills = "Primary Skills are required";
    }

    if (!data.role.trim()) {
      newErrors.role = "Role is required";
    }

    if (data.aadharNo.trim() && !/^\d{12}$/.test(data.aadharNo.replace(/\s/g, ''))) {
      newErrors.aadharNo = "Please enter a valid 12-digit Aadhar number";
    }

    if (data.freelanceRate && !/^[0-9]+$/.test(data.freelanceRate)) {
      newErrors.freelanceRate = "Freelance Rate / Hours must contain only numbers";
    }

    if (data.noticePeriod && !/^[0-9]+$/.test(data.noticePeriod)) {
      newErrors.noticePeriod = "Notice Period must contain only numbers";
    } else if (data.noticePeriod && parseInt(data.noticePeriod, 10) > 90) {
      newErrors.noticePeriod = "Notice Period cannot exceed 90 days";
    }

    if (data.expectedC2CRate && !/^[0-9]+$/.test(data.expectedC2CRate)) {
      newErrors.expectedC2CRate = "Expected CTC must contain only numbers";
    } else if (data.expectedC2CRate && parseInt(data.expectedC2CRate, 10) > 999999) {
      newErrors.expectedC2CRate = "Expected CTC cannot exceed 999999";
    }

    if (data.c2c && !/^[0-9]+$/.test(data.c2c)) {
      newErrors.c2c = 'Current CTC must contain only numbers';
    } else if (data.c2c && parseInt(data.c2c, 10) > 999999) {
      newErrors.c2c = 'Current CTC cannot exceed 999999';
    }

    if (data.pancardNo.trim() && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(data.pancardNo.toUpperCase())) {
      newErrors.pancardNo = "Please enter a valid PAN Card number (e.g., ABCDE1234F)";
    }

    // File validation - only required if NOT in edit mode
    if (!isEditMode && !file) {
      newErrors.file = "Resume file is required";
    } else if (!isEditMode && file) {
      const allowedTypes = ["application/pdf", "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      const allowedExtensions = [".pdf", ".doc", ".docx"];
      const maxFileSize = 5 * 1024 * 1024; // 5MB

      // Check file type
      if (!allowedTypes.includes(file.type)) {
        newErrors.file = "Only PDF, DOC, and DOCX files are allowed";
      }
      // Check extension
      else if (!allowedExtensions.includes(
        "." + file.name.split(".").pop().toLowerCase()
      )) {
        newErrors.file = "Invalid file type. Only PDF, DOC, DOCX allowed";
      }
      // Check file size
      else if (file.size > maxFileSize) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        newErrors.file = `File size (${sizeMB}MB) exceeds 5MB limit`;
      }
      // Check empty file
      else if (file.size === 0) {
        newErrors.file = "File is empty";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const fieldOrder = [
        "fullName",
        "email",
        "mobile",
        "currentLocation",
        "role",
        "yearsOfExperience",
        "primarySkills",
        "freelanceRate",
        "referrerCode",
        "source",
        "availableFrom",
        "file",
      ];
      const firstField = fieldOrder.find((field) => newErrors[field]);
      const firstMessage = newErrors[firstField] || "Please fix the highlighted fields.";
      setPopupData({ visible: true, message: firstMessage, targetId: firstField || "fullName" });
      return false;
    }

    return true;
  };

  // ============================================================
  // EVENT HANDLERS
  // ============================================================

  /**
   * Handle text input changes
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked, options } = e.target;
    let newValue = type === 'checkbox'
      ? checked
      : type === 'select-multiple'
        ? Array.from(options).filter((option) => option.selected).map((option) => option.value)
        : value;

    // Force numeric-only values for specific fields
    const numericOnlyFields = ['mobile', 'freelanceRate', 'noticePeriod', 'expectedC2CRate', 'c2c'];
    const decimalFields = ['yearsOfExperience'];
    if (numericOnlyFields.includes(name) && typeof newValue === 'string') {
      newValue = newValue.replace(/\D/g, '');
      if (name === 'freelanceRate' && newValue) {
        const numericValue = parseInt(newValue, 10);
        if (numericValue > 9999) {
          newValue = '9999';
        }
      }
      if (name === 'noticePeriod' && newValue) {
        const numericValue = parseInt(newValue, 10);
        if (numericValue > 90) {
          newValue = '90';
        }
      }
      if ((name === 'c2c' || name === 'expectedC2CRate') && newValue) {
        const numericValue = parseInt(newValue, 10);
        if (numericValue > 999999) {
          newValue = '999999';
        }
      }
    } else if (decimalFields.includes(name) && typeof newValue === 'string') {
      newValue = newValue.replace(/[^0-9.]/g, '');
      if (name === 'yearsOfExperience' && newValue) {
        const numericValue = parseFloat(newValue);
        if (numericValue > 30) {
          newValue = '30';
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleToggleSelect = (name, value) => {
    setFormData((prev) => {
      const currentValues = Array.isArray(prev[name]) ? prev[name] : [];
      let nextValues;

      if (currentValues.includes(value)) {
        nextValues = currentValues.filter((item) => item !== value);
      } else {
        nextValues = currentValues.filter((item) => item !== 'Not specified');
        nextValues = [...nextValues, value];
      }

      return {
        ...prev,
        [name]: nextValues,
      };
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const filteredSecondarySkills = secondarySkillOptions.filter((skill) =>
    skill.toLowerCase().includes(secondarySearch.toLowerCase())
  );

  const filteredWorkTypeOptions = workTypeOptions.filter((type) =>
    type.toLowerCase().includes(workTypeSearch.toLowerCase())
  );

  /**
   * Handle file selection
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFilePreview({
        name: selectedFile.name,
        size: (selectedFile.size / 1024).toFixed(2),
        type: selectedFile.type,
      });
      // Clear file error
      if (errors.file) {
        setErrors((prev) => ({
          ...prev,
          file: "",
        }));
      }
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    console.log("🚀 Form submission started");
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    const authCode = authReferrerCode;
    let submissionData = formData;
    if (!isEditMode && !submissionData.referrerCode && authCode) {
      submissionData = { ...submissionData, referrerCode: authCode };
      setFormData(submissionData);
    }

    // Validate form before submitting
    console.log("🔍 Validating form...");
    if (!validateForm(submissionData)) {
      console.log("❌ Form validation failed");
      return;
    }
    console.log("✅ Form validation passed");

    // Show loading popup
    const loadingMessage = isEditMode ? "Updating candidate..." : "Processing registration...";
    setLoadingPopup({ visible: true, message: loadingMessage });
    setIsLoading(true);

    try {
      console.log("📦 Preparing FormData...");
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(submissionData).forEach(key => {
        const value = Array.isArray(submissionData[key]) ? submissionData[key].join(', ') : submissionData[key];
        formDataToSend.append(key, value);
        console.log(`📝 Added field: ${key} = ${value}`);
      });

      // Add file only if not in edit mode and file exists
      if (!isEditMode && file) {
        formDataToSend.append("resumeFile", file);
        console.log(`📎 Added file: ${file.name} (${file.size} bytes)`);
      }

      let response;
      if (isEditMode) {
        console.log("🌐 Calling updateCandidate API...");
        // Call API to update candidate
        response = await updateCandidate(candidateData.id, formDataToSend);
      } else {
        console.log("🌐 Calling registerCandidate API...");
        // Call API to register candidate
        response = await registerCandidate(formDataToSend);
      }
      console.log("📡 API response received:", response);

      if (response.success) {
        const successMsg = isEditMode ? "Candidate updated successfully" : "Candidate registered successfully";
        console.log(`✅ ${successMsg}`, response.data);

        // Hide loading popup and show success popup
        setLoadingPopup({ visible: false });
        setSuccessPopup({
          visible: true,
          message: `${successMsg}!`,
          candidateId: isEditMode ? candidateData.id : response.data.candidateId
        });

        // Reset form only if not in edit mode
        if (!isEditMode) {
          setFormData({
            fullName: "",
            email: "",
            mobile: "",
            currentLocation: "",
            readyToTravel: "No",
            yearsOfExperience: "",
            primarySkills: "",
            secondarySkills: [],
            techStack: "",
            workType: [],
            noticePeriod: "",
            c2c: "No",
            expectedC2CRate: "",
            freelanceRate: "",
            referrerCode: "",
            source: "",
            status: "Active",
            role: "",
            aadharNo: "",
            pancardNo: "",
            remoteAvailable: "No",
            availableFrom: "",
          });
          setFile(null);
          setFilePreview(null);
          setErrors({});
        }
      } else {
        const errorType = isEditMode ? "update" : "registration";
        console.log(`❌ ${errorType} failed:`, response.message);
        // Hide loading popup and show error message
        setLoadingPopup({ visible: false });
        setErrorMessage(`❌ ${response.message || errorType + ' failed'}`);
      }
    } catch (error) {
      console.error("Candidate submission error:", error);
      // Hide loading popup and show error message
      setLoadingPopup({ visible: false });
      setErrorMessage("❌ An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle popup close
   */
  const handlePopupClose = () => {
    setPopupData((prev) => ({ ...prev, visible: false }));

    if (popupData.targetId) {
      const targetElement = document.getElementById(popupData.targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof targetElement.focus === "function") {
          targetElement.focus();
        }
      }
    }
  };

  const handleSuccessPopupClose = () => {
    setSuccessPopup({ visible: false, message: "", candidateId: "" });
    if (isEditMode) {
      navigate('/candidates');
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="resume-page-wrapper">
      <div className="resume-container">
        {/* Header */}
        <div className="resume-header">
          <h1 className="resume-title">
            {isEditMode ? "Update Candidate Details" : "Candidate Registration"}
          </h1>
          <p className="resume-subtitle">
            {isEditMode 
              ? "Update your candidate details below." 
              : "Fill in your details to register as a candidate. Candidate ID will be auto-generated."}
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="alert alert-success">
            <div className="alert-content">{successMessage}</div>
            <button
              className="alert-close"
              onClick={() => setSuccessMessage("")}
            >
              ×
            </button>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="alert alert-error">
            <div className="alert-content">{errorMessage}</div>
            <button
              className="alert-close"
              onClick={() => setErrorMessage("")}
            >
              ×
            </button>
          </div>
        )}

        {/* Loading Popup */}
        <PopupMessage
          visible={loadingPopup.visible}
          title="Processing"
          message={loadingPopup.message}
          onClose={() => { }} // No close button for loading
          type="info"
          showButton={false}
          showSpinner={true}
        />

        {/* Success Popup */}
        <PopupMessage
          visible={successPopup.visible}
          title="Success"
          message={`${successPopup.message} Candidate ID: ${successPopup.candidateId}`}
          onClose={handleSuccessPopupClose}
          type="success"
        />

        <PopupMessage
          visible={popupData.visible}
          title="Please complete the form"
          message={popupData.message}
          onClose={handlePopupClose}
          type="error"
        />
        <div className="resume-info">
          <h3>Information</h3>
          <ul>
            <li>• All required fields are marked with <span className="required">*</span></li>
            {!isEditMode && (
              <>
                <li>• Candidate ID will be auto-generated by the system</li>
                <li>• Supported file formats: PDF, DOC, DOCX (Max 5MB)</li>
              </>
            )}
            <li>• Your information will be securely stored</li>
            <li>• Aadhar and PAN Card numbers are optional, but if entered they must be valid</li>
          </ul>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="candidate-form">
          {/* Personal Information */}
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="fullName" className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter your full name (as per Aadhar)"
                  className={`form-input ${errors.fullName ? "input-error" : ""}`}
                  disabled={isLoading}
                />
                {errors.fullName && (
                  <p className="error-text">{errors.fullName}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@gmail.com"
                  className={`form-input ${errors.email ? "input-error" : ""}`}
                  disabled={isLoading}
                />
                {errors.email && (
                  <p className="error-text">{errors.email}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mobile" className="form-label">
                  Mobile <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="10-digit Indian mobile number (e.g., 9876543210)"
                  className={`form-input ${errors.mobile ? "input-error" : ""}`}
                  disabled={isLoading}
                />
                {errors.mobile && (
                  <p className="error-text">{errors.mobile}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="currentLocation" className="form-label">
                  Current Location <span className="required">*</span>
                </label>
                <select
                  id="currentLocation"
                  name="currentLocation"
                  value={formData.currentLocation}
                  onChange={handleInputChange}
                  className={`form-input ${errors.currentLocation ? "input-error" : ""}`}
                  disabled={isLoading}
                >
                  <option value="">Select Current Location</option>
                  {currentLocations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                {errors.currentLocation && (
                  <p className="error-text">{errors.currentLocation}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="role" className="form-label">
                  Role <span className="required">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={`form-input ${errors.role ? "input-error" : ""}`}
                  disabled={isLoading}
                >
                  <option value="">Select a role...</option>
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="error-text">{errors.role}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="aadharNo" className="form-label">
                  Aadhar Number
                </label>
                <input
                  type="text"
                  id="aadharNo"
                  name="aadharNo"
                  value={formData.aadharNo}
                  onChange={handleInputChange}
                  placeholder="12-digit Aadhar number"
                  className={`form-input ${errors.aadharNo ? "input-error" : ""}`}
                  disabled={isLoading}
                  maxLength="12"
                />
                {errors.aadharNo && (
                  <p className="error-text">{errors.aadharNo}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="pancardNo" className="form-label">
                  PAN Card Number
                </label>
                <input
                  type="text"
                  id="pancardNo"
                  name="pancardNo"
                  value={formData.pancardNo}
                  onChange={handleInputChange}
                  placeholder="ABCDE1234F"
                  className={`form-input form-input-uppercase ${errors.pancardNo ? "input-error" : ""}`}
                  disabled={isLoading}
                  maxLength="10"
                />
                {errors.pancardNo && (
                  <p className="error-text">{errors.pancardNo}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="readyToTravel" className="form-label">
                  Ready to Travel
                </label>
                <select
                  id="readyToTravel"
                  name="readyToTravel"
                  value={formData.readyToTravel}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="remoteAvailable" className="form-label">
                  Remote Available
                </label>
                <select
                  id="remoteAvailable"
                  name="remoteAvailable"
                  value={formData.remoteAvailable}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="availableFrom" className="form-label">
                  Available From
                </label>
                <input
                  type="date"
                  id="availableFrom"
                  name="availableFrom"
                  value={formData.availableFrom}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="freelanceRate" className="form-label">
                  Freelance Rate / Hours
                </label>
                <input
                  type="number"
                  id="freelanceRate"
                  name="freelanceRate"
                  value={formData.freelanceRate}
                  onChange={handleInputChange}
                  placeholder="e.g., 200"
                  className={`form-input ${errors.freelanceRate ? 'input-error' : ''}`}
                  disabled={isLoading}
                  min="0"
                  max="9999"
                  step="1"
                  inputMode="numeric"
                />
                {errors.freelanceRate && (
                  <p className="error-text">{errors.freelanceRate}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="referrerCode" className="form-label">
                  Referrer Code
                </label>
                <input
                  type="text"
                  id="referrerCode"
                  name="referrerCode"
                  value={formData.referrerCode}
                  onChange={handleInputChange}
                  placeholder="Referral code if any"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="source" className="form-label">
                  Source
                </label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isLoading}
                >
                  <option value="">Select Source</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Naukri">Naukri</option>
                  <option value="Indeed">Indeed</option>
                  <option value="Monster">Monster India</option>
                  <option value="Referral">Referral</option>
                  <option value="Direct">Direct Application</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="form-section">
            <h3>Professional Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="yearsOfExperience" className="form-label">
                  Years of Experience <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="yearsOfExperience"
                  name="yearsOfExperience"
                  value={formData.yearsOfExperience}
                  onChange={handleInputChange}
                  placeholder="e.g., 3 or 3.6"
                  min="0"
                  max="30"
                  step="0.1"
                  className={`form-input ${errors.yearsOfExperience ? 'input-error' : ''}`}
                  disabled={isLoading}
                  inputMode="decimal"
                />
                {errors.yearsOfExperience && (
                  <p className="error-text">{errors.yearsOfExperience}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="primarySkills" className="form-label">
                  Primary Skills <span className="required">*</span>
                </label>
                <select
                  id="primarySkills"
                  name="primarySkills"
                  value={formData.primarySkills}
                  onChange={handleInputChange}
                  className={`form-input ${errors.primarySkills ? "input-error" : ""}`}
                  disabled={isLoading}
                >
                  <option value="">Select Primary Skill</option>
                  {primarySkillOptions.map((skill) => (
                    <option key={skill} value={skill}>
                      {skill}
                    </option>
                  ))}
                </select>
                {errors.primarySkills && (
                  <p className="error-text">{errors.primarySkills}</p>
                )}
              </div>



              <div className="form-group">
                <label htmlFor="techStack" className="form-label">
                  Tech Stack
                </label>
                <input
                  type="text"
                  id="techStack"
                  name="techStack"
                  value={formData.techStack}
                  onChange={handleInputChange}
                  placeholder="e.g., MERN, MEAN, LAMP"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="noticePeriod" className="form-label">
                  Notice Period in days
                </label>
                <input
                  type="number"
                  id="noticePeriod"
                  name="noticePeriod"
                  value={formData.noticePeriod}
                  onChange={handleInputChange}
                  placeholder="e.g., 30"
                  className={`form-input ${errors.noticePeriod ? 'input-error' : ''}`}
                  disabled={isLoading}
                  min="0"
                  max="90"
                  step="1"
                  inputMode="numeric"
                />
                {errors.noticePeriod && (
                  <p className="error-text">{errors.noticePeriod}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="c2c" className="form-label">
                  Current CTC / Month (in INR)
                </label>
                <input
                  type="number"
                  id="c2c"
                  name="c2c"
                  value={formData.c2c}
                  onChange={handleInputChange}
                  placeholder="e.g., 50000/month"
                  className={`form-input ${errors.c2c ? 'input-error' : ''}`}
                  disabled={isLoading}
                  min="0"
                  max="999999"
                  inputMode="numeric"
                />
                {errors.c2c && (
                  <p className="error-text">{errors.c2c}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="expectedC2CRate" className="form-label">
                  Expected CTC / Month (in INR)
                </label>
                <input
                  type="number"
                  id="expectedC2CRate"
                  name="expectedC2CRate"
                  value={formData.expectedC2CRate}
                  onChange={handleInputChange}
                  placeholder="e.g., 50000/month"
                  className={`form-input ${errors.expectedC2CRate ? 'input-error' : ''}`}
                  disabled={isLoading}
                  min="0"
                  max="999999"
                  inputMode="numeric"
                />
                {errors.expectedC2CRate && (
                  <p className="error-text">{errors.expectedC2CRate}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Secondary Skills
                </label>
                <div ref={secondaryDropdownRef} className={`custom-multi-dropdown ${errors.secondarySkills ? 'input-error' : ''}`}>
                  <button
                    type="button"
                    className="dropdown-header"
                    onClick={() => setSecondaryOpen((prev) => !prev)}
                    disabled={isLoading}
                  >
                    <span>
                      {formData.secondarySkills.length > 0
                        ? formData.secondarySkills.join(', ')
                        : 'Select one or more secondary skills'}
                    </span>
                    <span className="dropdown-arrow">{secondaryOpen ? '▲' : '▼'}</span>
                  </button>

                  {secondaryOpen && (
                    <div className="dropdown-panel">
                      <input
                        type="text"
                        className="dropdown-search"
                        placeholder="Search Secondary Skills"
                        value={secondarySearch}
                        onChange={(e) => setSecondarySearch(e.target.value)}
                        disabled={isLoading}
                      />
                      <div className="dropdown-options">
                        {filteredSecondarySkills.length > 0 ? (
                          filteredSecondarySkills.map((skill) => (
                            <label key={skill} className="dropdown-option">
                              <input
                                type="checkbox"
                                checked={formData.secondarySkills.includes(skill)}
                                onChange={() => handleToggleSelect('secondarySkills', skill)}
                                disabled={isLoading}
                              />
                              {skill}
                            </label>
                          ))
                        ) : (
                          <div className="dropdown-empty">No skills found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.secondarySkills && (
                  <p className="error-text">{errors.secondarySkills}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Work Type
                </label>
                <div ref={workTypeDropdownRef} className={`custom-multi-dropdown ${errors.workType ? 'input-error' : ''}`}>
                  <button
                    type="button"
                    className="dropdown-header"
                    onClick={() => setWorkTypeOpen((prev) => !prev)}
                    disabled={isLoading}
                  >
                    <span>
                      {formData.workType.length > 0
                        ? formData.workType.join(', ')
                        : 'Select one or more work types'}
                    </span>
                    <span className="dropdown-arrow">{workTypeOpen ? '▲' : '▼'}</span>
                  </button>

                  {workTypeOpen && (
                    <div className="dropdown-panel">
                      <input
                        type="text"
                        className="dropdown-search"
                        placeholder="Search Work Type"
                        value={workTypeSearch}
                        onChange={(e) => setWorkTypeSearch(e.target.value)}
                        disabled={isLoading}
                      />
                      <div className="dropdown-options">
                        {filteredWorkTypeOptions.length > 0 ? (
                          filteredWorkTypeOptions.map((type) => (
                            <label key={type} className="dropdown-option">
                              <input
                                type="checkbox"
                                checked={formData.workType.includes(type)}
                                onChange={() => handleToggleSelect('workType', type)}
                                disabled={isLoading}
                              />
                              {type}
                            </label>
                          ))
                        ) : (
                          <div className="dropdown-empty">No work types found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {errors.workType && (
                  <p className="error-text">{errors.workType}</p>
                )}
              </div>


            </div>
          </div>

          {/* Resume Upload - Hidden in Edit Mode */}
          {!isEditMode && (
            <div className="form-section">
              <h3>Resume Upload</h3>
              <div className="form-group">
                <label htmlFor="file" className="form-label">
                  Resume File <span className="required">*</span>
                </label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="file"
                    onChange={handleFileChange}
                    accept=".pdf, .doc, .docx"
                    className="file-input"
                    disabled={isLoading}
                  />
                  <div className="file-upload-label">
                    <div className="file-icon"></div>
                    <div className="file-text">
                      <p className="file-main">
                        {filePreview
                          ? `Selected: ${filePreview.name}`
                          : "Click to upload or drag and drop"}
                      </p>
                      <p className="file-sub">
                        PDF, DOC, DOCX • Max 5MB
                      </p>
                    </div>
                  </div>
                </div>
                {filePreview && (
                  <div className="file-preview">
                    <p>
                      <strong>File:</strong> {filePreview.name}
                    </p>
                    <p>
                      <strong>Size:</strong> {filePreview.size} KB
                    </p>
                  </div>
                )}
                {errors.file && (
                  <p className="error-text">{errors.file}</p>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  {isEditMode ? "Updating..." : "Registering..."}
                </>
              ) : (
                isEditMode ? "Update Candidate" : "Register as Candidate"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResumePage;
